"use client";

import React from "react";
import { Control, Controller } from "react-hook-form";
import {
  Box,
  FormControl,
  FormHelperText,
  Stack,
  TextField,
  Typography,
  useMediaQuery
} from "@mui/material";
import { useLocale, useTranslations } from "next-intl";
import isEmail from "@/utils/validation/is-email-util";
import { validatePhoneByLocale } from "@/utils/validation/verify-phone-by-locale";
import { ColletteCheckbox, ColletteButton } from "@collette-travel/collette-common-controls";
import { Locale } from "@/i18n/routing";
import ManagedReCAPTCHA from "@/components/managed-recaptcha/managed-recaptcha";
import {RACFormData} from "@/types/leads/lead-forms";

interface RACFormProps {
  control: Control<RACFormData, any>;
  handleSubmit: React.FormEventHandler<HTMLFormElement> | undefined;
  isInvalid: boolean;
  isSubmitting: boolean;
}

const RequestACallbackForm = ({ control, handleSubmit, isInvalid, isSubmitting }: RACFormProps) => {
  const t = useTranslations("FORMS");
  const locale = useLocale() as Locale;
  const shrinkReCaptcha = useMediaQuery("(max-width:425px)");

  return (
    <Box
      component="form"
      noValidate
      onSubmit={handleSubmit}
      data-testid="rac-form"
      data-form-name="Request a Callback"
      data-form-id="request-a-callback-form"
      data-track="visibility"
    >
      <Stack
        sx={{
          flexDirection: "column",
          gap: "1.6rem",
          padding: { xs: "2rem 2.4rem", md: "2.4rem 4rem" }
        }}
      >
        <Typography component="p" variant="h6" data-testid="rac-contact-section-header">
          {t("Contact")}
        </Typography>
        <Stack
          component="fieldset"
          sx={{
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: "1.6rem", sm: "3.2rem" },
            border: "none",
            padding: 0
          }}
        >
          <Controller
            name="firstName"
            control={control}
            rules={{
              required: t("Please complete this required field")
            }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                required
                slotProps={{ htmlInput: { "data-testid": "customer-care-form-first-name-input" } }}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                aria-label={t("First Name")}
                placeholder={t("First Name")}
                label={t("First Name")}
                sx={{
                  width: { xs: "100%", sm: "50%" }
                }}
              />
            )}
          />
          <Controller
            name="lastName"
            control={control}
            rules={{
              required: t("Please complete this required field")
            }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                required
                slotProps={{ htmlInput: { "data-testid": "customer-care-form-last-name-input" } }}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                aria-label={t("Last Name")}
                placeholder={t("Last Name")}
                label={t("Last Name")}
                sx={{
                  width: { xs: "100%", sm: "50%" }
                }}
              />
            )}
          />
        </Stack>
        <Stack
          component="fieldset"
          sx={{
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: "1.6rem", sm: "3.2rem" },
            border: "none",
            padding: 0
          }}
        >
          <Controller
            name="email"
            control={control}
            rules={{
              required: t("Please complete this required field"),
              validate: (value) => isEmail(value) || t("Email must be formatted correctly")
            }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                required
                slotProps={{ htmlInput: { "data-testid": "customer-care-form-email-input" } }}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                aria-label={t("Email")}
                placeholder={t("Email")}
                label={t("Email")}
                sx={{
                  width: { xs: "100%", sm: "50%" }
                }}
              />
            )}
          />
          <Controller
            name="phone"
            control={control}
            rules={{
              required: t("Please complete this required field"),
              validate: (value) => validatePhoneByLocale(value, locale, t)
            }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                required
                slotProps={{ htmlInput: { "data-testid": "customer-care-form-phone-input" } }}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                aria-label={t("Phone")}
                placeholder={t("Phone")}
                label={t("Phone")}
                sx={{
                  width: { xs: "100%", sm: "50%" }
                }}
              />
            )}
          />
        </Stack>
        <Stack gap={1}>
          <Controller
            name="isTravelAgent"
            control={control}
            render={({ field }) => (
              <ColletteCheckbox
                {...field}
                checked={!!field.value}
                id="customer-care-form-travel-prof-checkbox"
                testId="customer-care-form-travel-prof-checkbox"
                label={t("I'm a travel professional")}
                onChange={(e) => field.onChange(e.target.checked)}
              />
            )}
          />
          <Controller
            name="workingWithAgent"
            control={control}
            render={({ field }) => (
              <ColletteCheckbox
                {...field}
                checked={!!field.value}
                testId="working-with-travel-professional-checkbox"
                id="working-with-travel-professional-checkbox"
                label={t("Working with travel professional")}
                onChange={(e) => field.onChange(e.target.checked)}
              />
            )}
          />
          <Controller
            name="withMoreTravelers"
            control={control}
            render={({ field }) => (
              <ColletteCheckbox
                {...field}
                checked={!!field.value}
                testId="traveling-with-8-or-more-travelers-checkbox"
                id="traveling-with-8-or-more-travelers-checkbox"
                label={t("Traveling with 8 or more people")}
                onChange={(e) => field.onChange(e.target.checked)}
              />
            )}
          />
        </Stack>
        <Controller
          name="recaptchaToken"
          control={control}
          rules={{
            required: t("Please complete this required field")
          }}
          render={({ field, fieldState }) => (
            <FormControl error={!!fieldState.error}>
              <Box
                sx={{
                  transformOrigin: "0 0",
                  transform: `scale(${shrinkReCaptcha ? "0.7" : "1" })`
                }}
              >
                <ManagedReCAPTCHA
                  size="normal"
                  sitekey="6LfV8zIpAAAAALFDQIb2-f4xUO3PrI8vVhrgwA9x"
                  onChange={(token) => field.onChange(token)}
                />
              </Box>
              <FormHelperText>{fieldState.error?.message}</FormHelperText>
            </FormControl>
          )}
        />

        {/* VALIDATION MESSAGE */}
        {isInvalid && (
          <Box
            sx={{
              padding: "0.4rem 1rem",
              backgroundColor: "error.100",
              borderRadius: "1rem",
              color: "error.dark"
            }}
          >
            <Typography>
              {t("Please complete all required fields")}
            </Typography>
          </Box>
        )}

        <Box width="100%" display="flex" justifyContent="end">
          <ColletteButton
            disabled={isSubmitting}
            label={t("Submit")}
            type="submit"
            variant="contained"
            color="primary"
            size="large"
            testId="customer-care-form-submit-button"
          />
        </Box>
      </Stack>
    </Box>
  );
};

export default RequestACallbackForm;
